import React from 'react';
import {
  ArenaGameState,
  CardGameType,
  SeatPosition,
  Direction,
  TrumpSuit,
  GamePhase,
  PlayedCard
} from '../../state/ArenaTypes';
import { GameTableConfig } from '../../engine/tableConfig/types';
import { DEFAULT_TIMING } from '../../engine/tableConfig/defaults';
import {
  IGameModule,
  GameTerminology,
  GameLabels,
  GameIcons,
  GameDisplayIndicators,
  GamePresentationResult,
  BotTurnResolution,
  BotTurnTimingContext
} from '../types';
import { GamePresentation } from '../../presentation/GamePresentationTypes';
import { extractNormalizedSeatsList } from '../common/seatUtils';

// Presentation components
import { BidWhistTableHud } from './presentation/BidWhistTableHud';
import { BidWhistCelebrationPill } from './presentation/BidWhistCelebrationPill';
import { BidWhistCelebrationOverlay } from './presentation/BidWhistCelebrationOverlay';
import { BiddingHud } from '../../components/Auction/BiddingHud';
import { BidWhistTrumpDeclarationHud } from '../../components/Auction/BidWhistTrumpDeclarationHud';
import { KittyDiscardHud } from '../../components/Kitty/KittyDiscardHud';
import { BidWhistScorePresenter } from './BidWhistScorePresenter';
import { BidWhistContractFormatter } from './BidWhistContractFormatter';
import { BidWhistEventSynthesizer } from './events/BidWhistEventSynthesizer';
import { BidWhistHandSortingStrategy } from './BidWhistHandSortingStrategy';
import { BidWhistReplayAdapter } from './BidWhistReplayAdapter';

export const BID_WHIST_PRESENTATION: GamePresentation = {
  Hud: BidWhistTableHud,
  CelebrationPill: BidWhistCelebrationPill,
  CelebrationOverlay: BidWhistCelebrationOverlay,
  get phases() {
    return {
      BIDDING: BiddingHud,
      DECLARE_TRUMP: BidWhistTrumpDeclarationHud,
      DECLARE: BidWhistTrumpDeclarationHud,
      KITTY_DISCARD: KittyDiscardHud,
    };
  },
  events: {},
};

/**
 * Bid Whist Game Module: Reference implementation of Client-Side Game Knowledge + Presentation.
 *
 * ARCHITECTURAL PRINCIPLE:
 * THE SERVER IS THE ONLY SOURCE OF TRUTH.
 *
 * The Bid Whist client module:
 *  - Understands Bid Whist terminology, labels, and icons/theme.
 *  - Selects Bid Whist presentation components (ArenaWinningBidHud, BiddingHud, etc.).
 *  - Formats authoritative contract and outcome text.
 *  - Projects authoritative server state into display indicators WITHOUT calculating authoritative facts.
 *
 * The Bid Whist client module MUST NEVER:
 *  - Calculate authoritative scores or books.
 *  - Determine legal plays or trick winners.
 *  - Determine whether a contract was made or set.
 *  - Determine whether Boston occurred.
 *  - Independently recreate Bid Whist rules or mutate authoritative game state.
 *
 * If client and server ever disagree:
 * SERVER STATE WINS.
 */
export class BidWhistGameModule implements IGameModule {
  public readonly gameType: CardGameType = 'BID_WHIST';
  public readonly displayName: string = 'Bid Whist';
  public readonly aliases: readonly string[] = [
    'SEVEN_IN_OUT',
    'UP_AND_DOWN',
    'NO_TRUMP',
    'BIDWHIST',
    'BID-WHIST',
    'BEST_OF_3',
    'BEST_OF_5',
    'BEST_OF_7'
  ];
  public readonly scorePresenter = new BidWhistScorePresenter();
  public readonly contractFormatter = new BidWhistContractFormatter();
  public readonly handSortingStrategy = new BidWhistHandSortingStrategy();
  public readonly presentation: GamePresentation = BID_WHIST_PRESENTATION;
  public readonly eventSynthesizer = new BidWhistEventSynthesizer();
  public readonly replayAdapter = new BidWhistReplayAdapter();

  // Bid Whist Specific Features & Contracts
  public readonly hasKitty = true;
  public readonly hasTrumpDeclaration = true;
  public readonly hasKittyDiscard = true;
  public readonly hasDirectionBidding = true;
  public readonly canEndHandEarlyOnSet = true;

  public getPhaseTimeoutSec(phase: GamePhase, config?: GameTableConfig | null): number {
    const timing = config?.timing ?? DEFAULT_TIMING;
    switch (phase) {
      case 'BIDDING':
        return timing.biddingTimeoutSec;
      case 'DECLARE_TRUMP':
        return timing.trumpDeclarationTimeoutSec;
      case 'KITTY_DISCARD':
        return timing.kittyDiscardTimeoutSec;
      case 'PLAYING':
      default:
        return timing.turnTimeoutSec;
    }
  }

  public getBotDelayMs(phase: GamePhase, config?: GameTableConfig | null): number {
    const timing = config?.timing ?? DEFAULT_TIMING;
    switch (phase) {
      case 'BIDDING':
        return timing.botBiddingDelayMs;
      case 'DECLARE_TRUMP':
      case 'KITTY_DISCARD':
        return timing.botTrumpDelayMs;
      case 'PLAYING':
      default:
        return timing.botPlayDelayMs;
    }
  }

  public getTeamForSeat(seat: SeatPosition): 1 | 2 {
    return (seat === 0 || seat === 2) ? 1 : 2;
  }

  public getPartnerSeat(seat: SeatPosition): SeatPosition {
    return ((seat + 2) % 4) as SeatPosition;
  }

  public getPlayerCount(): number {
    return 4;
  }

  public isPrePlayPhase(phase: GamePhase): boolean {
    return phase === 'LOBBY' || phase === 'DEALING' || phase === 'BIDDING' ||
      phase === 'DECLARE_TRUMP' || phase === 'KITTY_DISCARD';
  }

  public isPostAuctionPhase(phase: GamePhase): boolean {
    return phase === 'DECLARE_TRUMP' || phase === 'KITTY_DISCARD' || phase === 'PLAYING' ||
      phase === 'TRICK_END' || phase === 'TRICK_RESOLVED' || phase === 'HAND_END' || phase === 'GAME_OVER';
  }

  public isSetupPhase(phase: GamePhase): boolean {
    return phase === 'DEALING' || phase === 'BIDDING' || phase === 'DECLARE_TRUMP' || phase === 'KITTY_DISCARD';
  }

  public isActiveGameplayPhase(phase: GamePhase): boolean {
    return phase === 'BIDDING' || phase === 'DECLARE_TRUMP' || phase === 'KITTY_DISCARD' || phase === 'PLAYING';
  }

  public readonly terminology: GameTerminology = {
    trickNoun: 'book',
    trickNounPlural: 'books',
    bidNoun: 'bid',
    trumpNoun: 'trump',
    kittyNoun: 'kitty',
    sweepNoun: 'Boston',
    penaltyNoun: 'deficit',
    partnerNoun: 'partner'
  };

  public readonly labels: GameLabels = {
    displayName: 'Bid Whist',
    scoreboardTitle: 'Bid Whist Deficit Scoreboard',
    team1Label: 'Team 1',
    team2Label: 'Team 2',
    winningBidLabel: 'Winning Bid',
    contractSummaryLabel: 'Contract',
    phaseDisplayNames: {
      LOBBY: 'Table Lobby',
      DEALING: 'Dealing Kitty & Hands',
      BIDDING: 'Auction Bidding',
      DECLARE_TRUMP: 'Trump Declaration',
      KITTY_DISCARD: 'Kitty Discard',
      PLAYING: 'Playing Book',
      TRICK_END: 'Book Evaluation',
      TRICK_RESOLVED: 'Book Complete',
      HAND_END: 'Hand Complete',
      GAME_OVER: 'Match Concluded'
    }
  };

  public readonly icons: GameIcons = {
    themeSymbol: '🔥',
    themeColor: 'red',
    iconType: 'flame',
    statusBadges: {
      uptown: '⬆',
      downtown: '⬇',
      noTrump: '🚫',
      boston: '🚂'
    }
  };

  public getDisplayIndicators(
    state: ArenaGameState,
    _config?: GameTableConfig | null
  ): GameDisplayIndicators {
    const isBoston = Boolean(state.isBoston ?? (state.gameExt as any)?.isBoston);
    const isSet = Boolean(state.isSet ?? (state.gameExt as any)?.isSet);
    const booksNeededToSet = state.booksToSet?.booksNeededToSet ?? null;
    const suit = state.trumpSuit ?? (state.gameExt as any)?.trumpSuit;
    const isNT = Boolean(state.isNoTrump ?? (state.gameExt as any)?.isNoTrump ?? (suit === 'NT' || suit === 'NoTrump'));

    return {
      trickWinnerSeat: (state.trickWinnerSeat !== undefined && state.trickWinnerSeat !== null)
        ? (Number(state.trickWinnerSeat) as SeatPosition)
        : null,
      isGameOver: Boolean(state.phase === 'GAME_OVER' || (state as any).isGameOver),
      matchWinner: state.matchWinner != null ? Number(state.matchWinner) : null,
      leadSuit: state.currentTrick && state.currentTrick.length > 0 && state.currentTrick[0]?.card
        ? state.currentTrick[0].card.suit
        : null,
      currentTurnSeat: (state.currentTurnSeat !== undefined && state.currentTurnSeat !== null)
        ? (Number(state.currentTurnSeat) as SeatPosition)
        : null,
      bidWhist: {
        winningBid: state.winningBid ?? (state.gameExt as any)?.winningBid ?? null,
        winningBidderSeat: (state.winningBidderSeat !== undefined && state.winningBidderSeat !== null)
          ? (Number(state.winningBidderSeat) as SeatPosition)
          : ((state.gameExt as any)?.winningBidderSeat !== undefined && (state.gameExt as any)?.winningBidderSeat !== null)
            ? (Number((state.gameExt as any).winningBidderSeat) as SeatPosition)
            : null,
        direction: (state.direction ?? (state.gameExt as any)?.direction ?? 'UP') as Direction,
        trumpSuit: (suit || null) as TrumpSuit | null,
        isNoTrump: isNT,
        booksNeededToSet,
        isSet,
        isBoston,
        bostonTeam: state.bostonTeam ?? (state.gameExt as any)?.bostonTeam ?? null,
        kittyCardsCount: state.kitty?.length || (state.gameExt as any)?.kitty?.length || 6,
        kittyRevealed: Boolean(state.kittyRevealedToWinner ?? (state.gameExt as any)?.kittyRevealedToWinner)
      }
    };
  }

  public getPresentation(
    state: ArenaGameState,
    config?: GameTableConfig | null
  ): GamePresentationResult {
    const indicators = this.getDisplayIndicators(state, config);
    const contractSummary = this.formatContract(state, config);
    const outcomeText = this.formatOutcome(state, config);

    return {
      ...this.presentation,
      contractSummary,
      outcomeText,
      indicators
    };
  }

  public formatContract(state: ArenaGameState, config?: GameTableConfig | null): string {
    const rawBid = state.winningBid ?? (state.gameExt as any)?.winningBid;
    if (!rawBid) return 'Waiting for Bids';
    const suit = state.trumpSuit ?? (state.gameExt as any)?.trumpSuit;
    const isNT = Boolean(state.isNoTrump ?? (state.gameExt as any)?.isNoTrump ?? (suit === 'NT' || suit === 'NoTrump'));
    return this.contractFormatter.formatContractName(
      rawBid,
      state.direction ?? (state.gameExt as any)?.direction,
      suit,
      isNT,
      undefined,
      config ?? undefined
    );
  }

  public formatOutcome(state: ArenaGameState, config?: GameTableConfig | null, context?: any): string {
    return this.contractFormatter.formatContractOutcome(state, context, config ?? undefined);
  }

  public mapRealtimeState(raw: unknown): Record<string, unknown> {
    if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
      return {};
    }
    const data = raw as Record<string, unknown>;

    const winningBid = typeof data.winningBid === 'number' && Number.isFinite(data.winningBid) ? data.winningBid : null;
    const winningBidderSeat = typeof data.winningBidderSeat === 'number' && Number.isFinite(data.winningBidderSeat)
      ? data.winningBidderSeat
      : null;
    const direction = typeof data.direction === 'string' ? data.direction : null;
    const trumpSuit = typeof data.trumpSuit === 'string' ? data.trumpSuit : null;
    const isNoTrump = typeof data.isNoTrump === 'boolean'
      ? data.isNoTrump
      : Boolean(trumpSuit === 'NT' || trumpSuit === 'NoTrump');

    return {
      winningBid,
      winningBidderSeat,
      direction,
      trumpSuit,
      isNoTrump,
      bids: Array.isArray(data.bids) ? data.bids : [],
      booksNeededToSet: typeof data.booksNeededToSet === 'number' && Number.isFinite(data.booksNeededToSet)
        ? data.booksNeededToSet
        : null,
      isSet: typeof data.isSet === 'boolean' ? data.isSet : false,
      isBoston: typeof data.isBoston === 'boolean' ? data.isBoston : false,
      bostonTeam: typeof data.bostonTeam === 'number' && Number.isFinite(data.bostonTeam) ? data.bostonTeam : null,
      kittyRevealed: typeof data.kittyRevealed === 'boolean'
        ? data.kittyRevealed
        : (typeof data.kittyRevealedToWinner === 'boolean' ? data.kittyRevealedToWinner : false),
      ...(data.booksToSet !== undefined ? { booksToSet: data.booksToSet } : {}),
      ...(data.contract !== undefined ? { contract: data.contract } : {}),
    };
  }

  public getBotTurn(state: ArenaGameState): BotTurnResolution | null {
    if (!state) return null;
    const seatsList = extractNormalizedSeatsList(state.seats);
    const phase = state.phase;

    let activeSeat: SeatPosition | null = null;
    let actionType: string | undefined;

    if (phase === 'BIDDING') {
      if (state.currentBidderSeat !== null && state.currentBidderSeat !== undefined) {
        activeSeat = Number(state.currentBidderSeat) as SeatPosition;
        actionType = 'BID';
      }
    } else if (phase === 'DECLARE_TRUMP' || phase === 'KITTY_DISCARD') {
      const winnerSeat = state.winningBidderSeat ?? state.currentTurnSeat ?? 0;
      activeSeat = Number(winnerSeat) as SeatPosition;
      actionType = phase;
    } else if (phase === 'PLAYING') {
      if (state.currentTurnSeat !== null && state.currentTurnSeat !== undefined) {
        activeSeat = Number(state.currentTurnSeat) as SeatPosition;
        actionType = 'PLAY';
      }
    }

    if (activeSeat === null || !seatsList?.[Number(activeSeat)]?.isBot) {
      return null;
    }

    const turnKey = `${state.handNumber}_${phase}_${activeSeat}_${state.completedTricks?.length || 0}_${state.currentTrick?.length || 0}_${state.bids?.length || 0}`;

    return {
      activeSeat,
      phase,
      turnKey,
      actionType
    };
  }

  public getBotTurnDelayMs(state: ArenaGameState, context?: BotTurnTimingContext): number {
    const timing = context?.config?.timing ?? DEFAULT_TIMING;
    const phase = state.phase;

    if (phase === 'BIDDING') {
      return timing.botBiddingDelayMs;
    }
    if (phase === 'KITTY_DISCARD') {
      return 140;
    }
    if (phase === 'DECLARE_TRUMP') {
      return timing.botTrumpDelayMs;
    }
    if (phase === 'PLAYING') {
      const completedCount = state.completedTricks?.length || 0;

      const isLeadingNewTrick = (state.currentTrick?.length || 0) === 0 || (state.currentTrick?.length || 0) >= 4;
      const isLeadingAfterCompletedTrick = isLeadingNewTrick && completedCount > 0;

      if (isLeadingAfterCompletedTrick) {
        const timeSinceTrickEnd = Date.now() - (context?.lastTrickEndTime || 0);
        const trickEndGracePeriod = Math.max(0, 960 - timeSinceTrickEnd);
        return Math.max(timing.botPlayDelayMs, 360, trickEndGracePeriod);
      }

      if (isLeadingNewTrick) {
        return Math.min(timing.botPlayDelayMs, 360);
      }

      // Mid-trick plays: steady metronome cadence measured from when the previous card landed
      const timeSinceLastCard = context?.lastCardPlayTime ? Math.max(0, Date.now() - context.lastCardPlayTime) : 0;
      const targetDispatchCadence = 200;
      return Math.max(50, targetDispatchCadence - timeSinceLastCard);
    }
    return timing.botPlayDelayMs;
  }

  public getLastActorSeat(state: ArenaGameState): SeatPosition | null {
    const phase = state.phase;
    if (phase === 'BIDDING' || phase === 'DECLARE_TRUMP' || phase === 'KITTY_DISCARD') {
      if (state.bids && state.bids.length > 0) {
        return Number(state.bids[state.bids.length - 1].seat) as SeatPosition;
      }
      return null;
    }

    const comp = state.completedTricks || [];
    const latestComp = comp.length > 0 ? comp[comp.length - 1] : null;
    const trickSeat = (state.currentTrick && state.currentTrick.length > 0)
      ? Number(state.currentTrick[state.currentTrick.length - 1].seat)
      : (latestComp?.winnerSeat !== null && latestComp?.winnerSeat !== undefined)
        ? Number(latestComp.winnerSeat)
        : (latestComp?.winningSeat !== null && latestComp?.winningSeat !== undefined)
          ? Number(latestComp.winningSeat)
          : (state.trickWinnerSeat !== null && state.trickWinnerSeat !== undefined)
            ? Number(state.trickWinnerSeat)
            : null;

    return trickSeat !== null && !isNaN(trickSeat) ? (trickSeat as SeatPosition) : null;
  }

  public getWarmupEndpoints(state: ArenaGameState, mySeat: SeatPosition | null): string[] | null {
    if (state.phase === 'DECLARE_TRUMP' && mySeat !== null && Number(state.winningBidderSeat) === Number(mySeat)) {
      return ['arenaDeclareTrump', 'arenaSubmitDiscards'];
    }
    return null;
  }
}

export const bidWhistModule = new BidWhistGameModule();
